// Enhanced autocompleteUtils.ts - Updated to work better with parser

import { Suggestion } from './types';
import { ParseResult } from './FilterAST';

export interface InsertionResult {
  newValue: string;
  newCursorPosition: number;
  shouldKeepOpen: boolean;
}

// Legacy interface for backward compatibility
interface LegacyToken {
  type: 'field' | 'operator' | 'value' | 'logical' | 'parenthesis';
  value: string;
  start: number;
  end: number;
  raw: string;
}

interface LegacyCursorContext {
  insideToken: boolean;
  tokenIndex: number;
  positionInToken: number;
}

/**
 * Enhanced insertion function that can work with both parser and legacy approaches
 */
export function insertSuggestionWithParser(
  suggestion: Suggestion,
  currentValue: string,
  cursorPosition: number,
  parseResult: ParseResult,
): InsertionResult {
  if (suggestion.isHint) {
    return {
      newValue: currentValue,
      newCursorPosition: cursorPosition,
      shouldKeepOpen: false,
    };
  }

  const { context } = parseResult;

  if (!context) {
    return insertAtCursor(suggestion, currentValue, cursorPosition);
  }

  let newValue = currentValue;
  let insertValue = suggestion.value;
  let newCursorPosition = cursorPosition;

  // Handle semantic operators with cursor positioning
  if (suggestion.type === 'operator' && insertValue.includes('|')) {
    const cursorIndex = insertValue.indexOf('|');
    insertValue = insertValue.replace('|', '');

    if (
      context.currentNode &&
      (context.currentState === 'EditingOperator' ||
        context.currentState === 'TypingOperator')
    ) {
      const node = context.currentNode;
      newValue =
        currentValue.slice(0, node.position.start) +
        insertValue +
        currentValue.slice(node.position.end);
      newCursorPosition = node.position.start + cursorIndex;
    } else {
      const result = insertAtCursorWithSpacing(
        insertValue,
        currentValue,
        cursorPosition,
      );
      newValue = result.newValue;
      newCursorPosition =
        result.beforeLength + (result.needsSpaceBefore ? 1 : 0) + cursorIndex;
    }

    return {
      newValue,
      newCursorPosition,
      shouldKeepOpen: false,
    };
  }

  // Add quotes for values, except when noQuotes flag is set
  if (suggestion.type === 'value' && !suggestion.noQuotes) {
    insertValue = `"${suggestion.value}"`;
  }

  // CRITICAL FIX: Handle partial token replacement based on currentState
  if (
    context.currentState === 'TypingField' &&
    suggestion.type === 'field' &&
    context.partialText
  ) {
    // Replace the partial field text
    const partialLength = context.partialText.length;
    const startPosition = cursorPosition - partialLength;

    newValue =
      currentValue.slice(0, startPosition) +
      insertValue +
      currentValue.slice(cursorPosition);
    newCursorPosition = startPosition + insertValue.length;
  } else if (
    context.currentState === 'TypingOperator' &&
    suggestion.type === 'operator' &&
    context.partialText
  ) {
    // Replace the partial operator text
    const partialLength = context.partialText.length;
    const startPosition = cursorPosition - partialLength;

    newValue =
      currentValue.slice(0, startPosition) +
      insertValue +
      currentValue.slice(cursorPosition);
    newCursorPosition = startPosition + insertValue.length;
  } else if (
    context.currentState === 'TypingValue' &&
    suggestion.type === 'value' &&
    context.partialText
  ) {
    // Replace the partial value text
    const partialLength = context.partialText.length;
    const startPosition = cursorPosition - partialLength;

    newValue =
      currentValue.slice(0, startPosition) +
      insertValue +
      currentValue.slice(cursorPosition);
    newCursorPosition = startPosition + insertValue.length;
  } else if (
    context.currentState === 'TypingLogicalOperator' &&
    suggestion.type === 'logical' &&
    context.partialText
  ) {
    // Replace the partial logical operator text
    const partialLength = context.partialText.length;
    const startPosition = cursorPosition - partialLength;

    newValue =
      currentValue.slice(0, startPosition) +
      insertValue +
      currentValue.slice(cursorPosition);
    newCursorPosition = startPosition + insertValue.length;
  }
  // Handle editing existing complete tokens
  else if (context.currentNode && context.currentState.startsWith('Editing')) {
    const node = context.currentNode;
    newValue =
      currentValue.slice(0, node.position.start) +
      insertValue +
      currentValue.slice(node.position.end);
    newCursorPosition = node.position.start + insertValue.length;
  }
  // Handle inserting at cursor when between tokens
  else {
    const result = insertAtCursorWithSpacing(
      insertValue,
      currentValue,
      cursorPosition,
    );
    newValue = result.newValue;
    newCursorPosition =
      result.beforeLength +
      (result.needsSpaceBefore ? 1 : 0) +
      insertValue.length;
  }

  // Add trailing space for non-value suggestions
  if (suggestion.type !== 'value' && !newValue.endsWith(' ')) {
    newValue = newValue.trimEnd() + ' ';
    newCursorPosition = newValue.length;
  }

  // Determine if we should keep autocomplete open
  const shouldKeepOpen = suggestion.type !== 'value';

  return {
    newValue,
    newCursorPosition,
    shouldKeepOpen,
  };
}

/**
 * Legacy insertion function for backward compatibility
 */
export function insertSuggestion(
  suggestion: Suggestion,
  currentValue: string,
  cursorPosition: number,
  currentToken: LegacyToken | null,
  cursorContext: LegacyCursorContext | null,
): InsertionResult {
  // Don't insert hints
  if (suggestion.isHint) {
    return {
      newValue: currentValue,
      newCursorPosition: cursorPosition,
      shouldKeepOpen: false,
    };
  }

  let newValue = currentValue;
  let insertValue = suggestion.value;
  let newCursorPosition = cursorPosition;

  // Handle semantic operators with cursor positioning
  if (suggestion.type === 'operator' && insertValue.includes('|')) {
    const cursorIndex = insertValue.indexOf('|');
    insertValue = insertValue.replace('|', '');

    if (currentToken && cursorContext?.insideToken) {
      // Replace the current token
      newValue =
        currentValue.slice(0, currentToken.start) +
        insertValue +
        currentValue.slice(currentToken.end);
      newCursorPosition = currentToken.start + cursorIndex;
    } else {
      // Insert at cursor position
      const result = insertAtCursorWithSpacing(
        insertValue,
        currentValue,
        cursorPosition,
      );
      newValue = result.newValue;
      newCursorPosition =
        result.beforeLength + (result.needsSpaceBefore ? 1 : 0) + cursorIndex;
    }

    return {
      newValue,
      newCursorPosition,
      shouldKeepOpen: false,
    };
  }

  // Add quotes for values, except when noQuotes flag is set
  if (suggestion.type === 'value' && !suggestion.noQuotes) {
    insertValue = `"${suggestion.value}"`;
  }

  // Check if we're replacing an incomplete quoted value
  const quoteContent = extractIncompleteQuoteContent(
    currentValue,
    cursorPosition,
  );
  const isInIncompleteQuote =
    !currentToken && suggestion.type === 'value' && quoteContent !== null;

  if (isInIncompleteQuote) {
    // Find the last unclosed quote and replace everything after it
    const textBeforeCursor = currentValue.substring(0, cursorPosition);
    const quoteContext = getQuoteContext(textBeforeCursor);

    if (quoteContext.quoteStartIndex >= 0) {
      // Replace from the quote onwards
      const beforeQuote = currentValue.substring(
        0,
        quoteContext.quoteStartIndex,
      );
      const afterCursor = currentValue.substring(cursorPosition);

      newValue = beforeQuote + insertValue + afterCursor;
      newCursorPosition = beforeQuote.length + insertValue.length;
    }
  } else if (currentToken && cursorContext?.insideToken) {
    // Replace the current token
    newValue =
      currentValue.slice(0, currentToken.start) +
      insertValue +
      currentValue.slice(currentToken.end);
    newCursorPosition = currentToken.start + insertValue.length;
  } else {
    // Insert at cursor position
    const result = insertAtCursorWithSpacing(
      insertValue,
      currentValue,
      cursorPosition,
    );
    newValue = result.newValue;
    newCursorPosition =
      result.beforeLength +
      (result.needsSpaceBefore ? 1 : 0) +
      insertValue.length;
  }

  // Add trailing space for non-value suggestions
  if (suggestion.type !== 'value' && !newValue.endsWith(' ')) {
    newValue = newValue.trimEnd() + ' ';
    newCursorPosition = newValue.length;
  }

  // Determine if we should keep autocomplete open
  const shouldKeepOpen = suggestion.type !== 'value';

  return {
    newValue,
    newCursorPosition,
    shouldKeepOpen,
  };
}

// Helper functions

function insertAtCursor(
  suggestion: Suggestion,
  currentValue: string,
  cursorPosition: number,
): InsertionResult {
  let insertValue = suggestion.value;

  if (suggestion.type === 'value') {
    insertValue = `"${suggestion.value}"`;
  }

  const result = insertAtCursorWithSpacing(
    insertValue,
    currentValue,
    cursorPosition,
  );

  return {
    newValue: result.newValue,
    newCursorPosition:
      result.beforeLength +
      (result.needsSpaceBefore ? 1 : 0) +
      insertValue.length,
    shouldKeepOpen: suggestion.type !== 'value',
  };
}

function insertAtCursorWithSpacing(
  insertValue: string,
  currentValue: string,
  cursorPosition: number,
): {
  newValue: string;
  beforeLength: number;
  needsSpaceBefore: boolean;
} {
  const beforeCursor = currentValue.slice(0, cursorPosition);
  const afterCursor = currentValue.slice(cursorPosition);

  // Add space before if needed
  const needsSpaceBefore =
    beforeCursor.length > 0 &&
    !beforeCursor.endsWith(' ') &&
    !beforeCursor.endsWith('(');

  const newValue =
    beforeCursor + (needsSpaceBefore ? ' ' : '') + insertValue + afterCursor;

  return {
    newValue,
    beforeLength: beforeCursor.length,
    needsSpaceBefore,
  };
}

// Quote context detection (copied from FilterTokenizer for compatibility)
function getQuoteContext(text: string): {
  isInsideQuote: boolean;
  quoteChar: '"' | "'" | null;
  quoteStartIndex: number;
  quoteContent: string;
} {
  let inSingleQuote = false;
  let inDoubleQuote = false;
  let lastQuoteStart = -1;
  let quoteChar: '"' | "'" | null = null;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const prevChar = i > 0 ? text[i - 1] : '';

    // Skip escaped quotes
    if (prevChar === '\\') {
      continue;
    }

    if (char === '"' && !inSingleQuote) {
      if (!inDoubleQuote) {
        inDoubleQuote = true;
        lastQuoteStart = i;
        quoteChar = '"';
      } else {
        inDoubleQuote = false;
        lastQuoteStart = -1;
        quoteChar = null;
      }
    } else if (char === "'" && !inDoubleQuote) {
      if (!inSingleQuote) {
        inSingleQuote = true;
        lastQuoteStart = i;
        quoteChar = "'";
      } else {
        inSingleQuote = false;
        lastQuoteStart = -1;
        quoteChar = null;
      }
    }
  }

  const isInsideQuote = inSingleQuote || inDoubleQuote;
  const quoteContent =
    isInsideQuote && lastQuoteStart >= 0
      ? text.substring(lastQuoteStart + 1)
      : '';

  return {
    isInsideQuote,
    quoteChar,
    quoteStartIndex: lastQuoteStart,
    quoteContent,
  };
}

function extractIncompleteQuoteContent(
  input: string,
  cursorPosition: number,
): string | null {
  const textBeforeCursor = input.substring(0, cursorPosition);
  const quoteContext = getQuoteContext(textBeforeCursor);

  return quoteContext.isInsideQuote ? quoteContext.quoteContent : null;
}

/**
 * Calculate dynamic height for suggestions dropdown
 */
export function calculateDropdownHeight(numItems: number): number {
  const ITEM_HEIGHT = 32;
  const MAX_ITEMS = 5;
  const MIN_HEIGHT = 100;

  if (numItems === 0) {
    return MIN_HEIGHT;
  }

  // Show max 5 items, then scroll
  const visibleItems = Math.min(numItems, MAX_ITEMS);
  return Math.max(MIN_HEIGHT, visibleItems * ITEM_HEIGHT + 20);
}

/**
 * Get suggestion type badge styles
 */
export function getSuggestionTypeStyles(type: string): string {
  switch (type) {
    case 'field':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case 'operator':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
    case 'value':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'logical':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  }
}

/**
 * Enhanced version that uses parse context for better display info
 */
export function getSuggestionDisplayInfoWithParser(
  suggestions: Suggestion[],
  parseResult: ParseResult,
  value: string,
  messages: any,
): {
  isShowingHints: boolean;
  headerText: string;
  emptyText: string;
} {
  const realSuggestions = suggestions.filter((s) => !s.isHint);
  const hints = suggestions.filter((s) => s.isHint);

  const hasRealSuggestions = realSuggestions.length > 0;
  const hasHints = hints.length > 0;

  let headerText: string;
  let emptyText: string;
  let isShowingHints: boolean;

  if (hasRealSuggestions) {
    headerText = `${messages.filterAutoCompleteSuggestions || 'Suggestions'} (${
      realSuggestions.length
    })`;
    emptyText =
      messages.filterAutoCompleteNoSuggestions || 'No suggestions available';
    isShowingHints = false;
  } else if (hasHints) {
    headerText = `${messages.filterAutoCompleteHints || 'Input Hints'} (${
      hints.length
    })`;
    emptyText =
      messages.filterAutoCompleteNoHints || 'No input hints available';
    isShowingHints = true;
  } else {
    const context = parseResult.context;
    const isActivelySearching =
      context?.expectsNext === 'Value' &&
      context?.currentNode?.type === 'Value';

    if (
      isActivelySearching ||
      (context?.expectsNext === 'Value' && value.includes('"'))
    ) {
      headerText = messages.filterAutoCompleteNoMatches || 'No matches found';
      emptyText =
        messages.filterAutoCompleteNoMatchesDescription ||
        'No suggestions match your search. Try a different term.';
      isShowingHints = false;
    } else {
      headerText =
        messages.filterAutoCompleteNoSuggestions || 'No suggestions available';
      emptyText =
        messages.filterAutoCompleteNoSuggestions || 'No suggestions available';
      isShowingHints = false;
    }
  }

  return { isShowingHints, headerText, emptyText };
}

/**
 * Legacy version for backward compatibility
 */
export function getSuggestionDisplayInfo(
  suggestions: Suggestion[],
  currentToken: any,
  expectedNext: string,
  value: string,
  messages: any,
): {
  isShowingHints: boolean;
  headerText: string;
  emptyText: string;
} {
  const realSuggestions = suggestions.filter((s) => !s.isHint);
  const hints = suggestions.filter((s) => s.isHint);

  const hasRealSuggestions = realSuggestions.length > 0;
  const hasHints = hints.length > 0;

  let headerText: string;
  let emptyText: string;
  let isShowingHints: boolean;

  if (hasRealSuggestions) {
    headerText = `${messages.filterAutoCompleteSuggestions || 'Suggestions'} (${
      realSuggestions.length
    })`;
    emptyText =
      messages.filterAutoCompleteNoSuggestions || 'No suggestions available';
    isShowingHints = false;
  } else if (hasHints) {
    headerText = `${messages.filterAutoCompleteHints || 'Input Hints'} (${
      hints.length
    })`;
    emptyText =
      messages.filterAutoCompleteNoHints || 'No input hints available';
    isShowingHints = true;
  } else {
    const isActivelySearching = currentToken && currentToken.type === 'value';
    if (
      isActivelySearching ||
      (expectedNext === 'Value' && value.includes('"'))
    ) {
      headerText = messages.filterAutoCompleteNoMatches || 'No matches found';
      emptyText =
        messages.filterAutoCompleteNoMatchesDescription ||
        'No suggestions match your search. Try a different term.';
      isShowingHints = false;
    } else {
      headerText =
        messages.filterAutoCompleteNoSuggestions || 'No suggestions available';
      emptyText =
        messages.filterAutoCompleteNoSuggestions || 'No suggestions available';
      isShowingHints = false;
    }
  }

  return { isShowingHints, headerText, emptyText };
}
